import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useMemo } from "react";
import { useAppLocale } from "./LocaleContext";
import { worklistCopyEn } from "@patient-watch/i18n/webapp/en-GB/worklistCopy";
import { worklistCopyDe } from "@patient-watch/i18n/webapp/de-DE/worklistCopy";
import { worklistCopyDa } from "@patient-watch/i18n/webapp/da-DK/worklistCopy";
import { worklistCopyEnUs } from "@patient-watch/i18n/webapp/en-US/worklistCopy";
import { worklistCopyFr } from "@patient-watch/i18n/webapp/fr-FR/worklistCopy";
import { worklistCopyIt } from "@patient-watch/i18n/webapp/it-IT/worklistCopy";
import { worklistCopyEs } from "@patient-watch/i18n/webapp/es-ES/worklistCopy";

export interface WorklistCopy {
    worklist: string;
    yourTasks: string;
    description: string;
    emptyTitle: string;
    emptyDescription: string;
    noMatchingTasks: string;
    noMatchingTasksDescription: string;
    complete: string;
    completeDetails: string;
    completeDetailsPanelDescription: string;
    signConsent: string;
    closePanel: string;
    noTaskSelected: string;
    noTaskSelectedDescription: string;
    openDiary: string;
    openPatient: string;
    toDo: string;
    refresh: string;
    forms: string;
    details: string;
    archived: string;
    archive: string;
    archiveAll: string;
    restoreAll: string;
    tasksSelected: (count: number) => string;
    selectedTasksDescription: string;
    selectTask: string;
    selectAllTasks: string;
    restore: string;
    archiveSucceeded: (count: number) => string;
    archiveFailed: string;
    restoreSucceeded: (count: number) => string;
    restoreFailed: string;
    emptyArchivedTitle: string;
    emptyArchivedDescription: string;
    allPeople: string;
    peopleFilterLabel: string;
    selectPeople: string;
    organisationFilterLabel: string;
    allOrganisations: string;
    taskTypeFilterLabel: string;
    allTaskTypes: string;
    searchTasks: string;
    clearSearch: string;
    filterTasks: string;
    filterTasksDescription: string;
    taskCompleteTitle: string;
    taskCompleteDescription: string;
    taskCompleteAllDoneTitle: string;
    taskCompleteAllDoneDescription: string;
    continueToNextTask: string;
    doNext: string;
    doNextDescription: (task: string, patientName: string, hospitalNumber: string | null) => string;
    nextPatientTask: (name: string) => string;
    returnToWorklist: string;
    patient: string;
    hospitalNumber: string;
    task: string;
    injectionDetailsTask: string;
    surgeryDetailsTask: string;
    shockwaveDetailsTask: string;
    procedureDetailsTask: string;
    consentTask: string;
    due: string;
    status: string;
    actions: string;
    dueLabel: string;
    blockedLabel: string;
    incompleteLabel: string;
    blockedBy: (blocker: string) => string;
    truncatedNotice: (total: number, shown: number) => string;
    unknownPatient: string;
    notDefined: string;
    loading: string;
    errorLoading: (message: string) => string;
    taskCount: (count: number) => string;
}

const worklistCopy: Record<SupportedLocale, WorklistCopy> = {
    "en-GB": worklistCopyEn,
    "en-US": worklistCopyEnUs,
    "de-DE": worklistCopyDe,
    "da-DK": worklistCopyDa,
    "fr-FR": worklistCopyFr,
    "it-IT": worklistCopyIt,
    "es-ES": worklistCopyEs,
};

export function getWorklistCopy(locale: SupportedLocale): WorklistCopy {
    return pickAppCopy(worklistCopy, locale);
}

export function useWorklistCopy(): WorklistCopy {
    const { locale } = useAppLocale();
    return useMemo(() => getWorklistCopy(locale), [locale]);
}
